import { DefinedDefaults } from "@/ui/lib/types";
import { ComponentType, HTMLAttributes, SVGProps } from "react";
import { getCustomIcon, getLucideIcon } from "./Icon.utils";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const ICON_COLOR_CLASS_MAP = {
  default: "fill-current",
} as const;

export const ICON_SIZE_CLASS_MAP = {
  default: "w-full",
  sm: "w-4",
  md: "w-6",
  lg: "w-8",
  xl: "w-11",
} as const;

const LUCIDE_ICON_KEY_MAP = {
  arrowRight: getLucideIcon("arrow-right"),
  externalLink: getLucideIcon("external-link"),
  downArrow: getLucideIcon("arrow-down"),
  leftArrow: getLucideIcon("arrow-left"),
  rightArrow: getLucideIcon("arrow-right"),
  upArrow: getLucideIcon("arrow-up"),
  tick: getLucideIcon("check"),
  circleTick: getLucideIcon("circle-check"),
  circleAlert: getLucideIcon("circle-alert"),
  triangleAlert: getLucideIcon("triangle-alert"),
  circleCheckBig: getLucideIcon("circle-check-big"),
  circleCross: getLucideIcon("circle-x"),
  circleUserRound: getLucideIcon("circle-user-round"),
  cross: getLucideIcon("x"),
  loader: getLucideIcon("loader-circle"),
  search: getLucideIcon("search"),
  settings: getLucideIcon("settings"),
  eye: getLucideIcon("eye"),
  eyeOff: getLucideIcon("eye-off"),
  chevronDown: getLucideIcon("chevron-down"),
  chevronUp: getLucideIcon("chevron-up"),
  chevronLeft: getLucideIcon("chevron-left"),
  chevronRight: getLucideIcon("chevron-right"),
  chevronUpDown: getLucideIcon("chevrons-up-down"),
  box: getLucideIcon("box"),
  chefHat: getLucideIcon("chef-hat"),
  truck: getLucideIcon("truck"),
  ellipsis: getLucideIcon("ellipsis"),
  info: getLucideIcon("info"),
  code: getLucideIcon("code-xml"),
  copy: getLucideIcon("copy"),
  refresh: getLucideIcon("rotate-cw"),
  history: getLucideIcon("history"),
  userRound: getLucideIcon("user-round"),
  creditCard: getLucideIcon("credit-card"),
  lockKeyhole: getLucideIcon("lock-keyhole"),
  circleHelp: getLucideIcon("circle-help"),
  logOut: getLucideIcon("log-out"),
  home: getLucideIcon("house"),
  fileHeart: getLucideIcon("file-heart"),
  cake: getLucideIcon("cake"),
  cat: getLucideIcon("cat"),
  usersRound: getLucideIcon("users-round"),
  gift: getLucideIcon("gift"),
  tag: getLucideIcon("tag"),
  trash: getLucideIcon("trash-2"),
  pending: getLucideIcon("loader"),
  pencil: getLucideIcon("pencil"),
  plus: getLucideIcon("plus"),
  mapPin: getLucideIcon("map-pin"),
  mars: getLucideIcon("mars"),
  venus: getLucideIcon("venus"),
  circleSmall: getLucideIcon("circle-small"),
} as const satisfies Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

const LIBRARY_ICON_KEY_MAP = {
  ...LUCIDE_ICON_KEY_MAP,
} as const satisfies Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

const CUSTOM_ICON_KEY_MAP = {
  bell: getCustomIcon(() => import("./custom-icons/Bell")),
  catProfile: getCustomIcon(() => import("./custom-icons/CatProfile")),
  filledCircleTick: getCustomIcon(() => import("./custom-icons/FilledCircleChecked")),
  send: getCustomIcon(() => import("./custom-icons/Send")),
  email: getCustomIcon(() => import("./custom-icons/Email")),
  facebook: getCustomIcon(() => import("./custom-icons/Facebook")),
  instagram: getCustomIcon(() => import("./custom-icons/Instagram")),
  filledGift: getCustomIcon(() => import("./custom-icons/Gift")),
  food: getCustomIcon(() => import("./custom-icons/Food")),
  litter: getCustomIcon(() => import("./custom-icons/Litter")),
  portion: getCustomIcon(() => import("./custom-icons/Portion")),
  youtube: getCustomIcon(() => import("./custom-icons/Youtube")),
  orderDelivery: getCustomIcon(() => import("./custom-icons/OrderDelivery")),
  orderExtras: getCustomIcon(() => import("./custom-icons/OrderExtras")),
  americanExpress: getCustomIcon(() => import("./custom-icons/AmericanExpress")),
  cartesBancaires: getCustomIcon(() => import("./custom-icons/CartesBancaires")),
  dinersClub: getCustomIcon(() => import("./custom-icons/DinersClub")),
  discover: getCustomIcon(() => import("./custom-icons/Discover")),
  eftpos: getCustomIcon(() => import("./custom-icons/Eftpos")),
  jcb: getCustomIcon(() => import("./custom-icons/JCB")),
  mastercard: getCustomIcon(() => import("./custom-icons/Mastercard")),
  paypal: getCustomIcon(() => import("./custom-icons/Paypal")),
  unionPay: getCustomIcon(() => import("./custom-icons/UnionPay")),
  visa: getCustomIcon(() => import("./custom-icons/Visa")),
  playingCat: getCustomIcon(() => import("./custom-icons/PlayingCat")),
  sleepingCat: getCustomIcon(() => import("./custom-icons/SleepingCat")),
  standingCat: getCustomIcon(() => import("./custom-icons/StandingCat")),
  vetAndCat: getCustomIcon(() => import("./custom-icons/VetAndCat")),
  flagGermany: getCustomIcon(() => import("./custom-icons/FlagGermany")),
  flagUnitedKingdom: getCustomIcon(() => import("./custom-icons/FlagUnitedKingdom")),
};

export const ICON_KEY_MAP = {
  ...LIBRARY_ICON_KEY_MAP,
  ...CUSTOM_ICON_KEY_MAP,
} as const satisfies Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

/*-------------------------------------
|  Types
|-------------------------------------*/

type TIconColor = keyof typeof ICON_COLOR_CLASS_MAP;
export type TIconKey = keyof typeof ICON_KEY_MAP;
type TIconSize = keyof typeof ICON_SIZE_CLASS_MAP;

export type TIconProps = {
  color?: TIconColor;
  icon: TIconKey;
  size?: TIconSize;
} & HTMLAttributes<HTMLDivElement>;

type TIconDefaultProps = DefinedDefaults<TIconProps, "size" | "color">;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const ICON_DEFAULTS = {
  size: "default",
  color: "default",
} satisfies TIconDefaultProps;
